import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import { normalizeMessage } from "./message-normalizer.ts";
import { messageMatchesSearchQuery } from "./search-match.ts";
import { buildTranscriptProjection, messageKey } from "./transcript.ts";

export type ChatTimelineOptions = {
  sessionKey: string;
  messages: unknown[];
  toolMessages: unknown[];
  streamSegments: Array<{ text: string; ts: number }>;
  stream: string | null;
  streamStartedAt: number | null;
  showToolCalls: boolean;
  showReasoning?: boolean;
  searchQuery?: string | null;
  historyRenderLimit?: number;
};

export const DEFAULT_CHAT_HISTORY_RENDER_LIMIT = 200;

export function buildChatTimelineItems(options: ChatTimelineOptions): ChatItem[] {
  const items: ChatItem[] = [];
  const history = Array.isArray(options.messages) ? options.messages : [];
  const tools = Array.isArray(options.toolMessages) ? options.toolMessages : [];
  const searchQuery = options.searchQuery?.trim() ?? "";
  const historyRenderLimit = options.historyRenderLimit ?? DEFAULT_CHAT_HISTORY_RENDER_LIMIT;
  const historyStart = Math.max(0, history.length - historyRenderLimit);

  if (historyStart > 0) {
    items.push({
      kind: "message",
      key: "chat:history:notice",
      message: {
        role: "system",
        content: `Showing last ${historyRenderLimit} messages (${historyStart} hidden).`,
        timestamp: Date.now(),
      },
    });
  }

  for (let i = historyStart; i < history.length; i++) {
    const message = history[i];
    const normalized = normalizeMessage(message);
    const raw = message as Record<string, unknown>;
    const marker = raw.__openclaw as Record<string, unknown> | undefined;

    if (marker?.kind === "compaction") {
      items.push({
        kind: "divider",
        key:
          typeof marker.id === "string"
            ? `divider:compaction:${marker.id}`
            : `divider:compaction:${normalized.timestamp}:${i}`,
        label: "Compaction",
        timestamp: normalized.timestamp ?? Date.now(),
      });
      continue;
    }

    if (searchQuery && !messageMatchesSearchQuery(message, searchQuery)) {
      continue;
    }

    items.push({
      kind: "message",
      key: messageKey(message, i),
      message,
    });
  }

  const segments = options.streamSegments ?? [];
  const maxLen = Math.max(segments.length, tools.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < segments.length && segments[i].text.trim().length > 0) {
      items.push({
        kind: "stream",
        key: `stream-seg:${options.sessionKey}:${i}`,
        text: segments[i].text,
        startedAt: segments[i].ts,
      });
    }
    if (i < tools.length && options.showToolCalls) {
      items.push({
        kind: "message",
        key: messageKey(tools[i], i + history.length),
        message: tools[i],
      });
    }
  }

  if (options.stream !== null) {
    const key = `stream:${options.sessionKey}:${options.streamStartedAt ?? "live"}`;
    if (options.stream.trim().length > 0) {
      items.push({
        kind: "stream",
        key,
        text: options.stream,
        startedAt: options.streamStartedAt ?? Date.now(),
      });
    } else {
      items.push({ kind: "reading-indicator", key });
    }
  }

  return items;
}

export function buildChatTimelineProjection(
  options: ChatTimelineOptions,
): Array<ChatItem | MessageGroup> {
  return buildTranscriptProjection(buildChatTimelineItems(options), {
    showToolCalls: options.showToolCalls,
    showReasoning: options.showReasoning ?? false,
  });
}
